/**
 * event-page.state.ts — the event page's own session model: the current
 * event's ticket types (read), the basket and contact drafts (the buyer's
 * own gestures), and every derived view the collapsed page renders from.
 * Pure state + computed()s only, no I/O — checkout.sync.ts is the sole writer
 * of shopTicketTypesState, the sole resetter of basketItemsState (on an
 * event switch) and its sole restorer (the cold-adoption restore, which puts
 * an adopted document's own payload.items back after that reset); every
 * other write here is a genuine user gesture — a qty stepper or a contact
 * keystroke through the exported actions below, a day chip straight onto
 * selectedProgrammeDayState.
 *
 * Every mirrored shape is derived from its @momo/types entity by indexed
 * access — BasketItem from
 * Checkout.Checkout's own payload.items element, ContactDraft from its
 * payload.buyer, price/volumeDiscounts/bucket from Event/TicketType's own
 * fields — never a hand-authored parallel interface.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { type Locale, resolveLocalizedText } from "@momo/i18n";
import {
	BookingFee,
	Checkout,
	type Event,
	type Location,
	Order,
	TicketType,
} from "@momo/types";
import type { TicketTypeWithId } from "@momo/views";
import { eventDetailState } from "@momo/views";
import { uiLanguage } from "../../i18n/localize.ts";
import { workspaceCard } from "../../workspace/workspace.state.ts";
import { locationsState } from "../locations.state.ts";
import { refundWindowPhase } from "../refund/window.state.ts";
import { appliedVoucher } from "../voucher/code.state.ts";

// ── Raw state ────────────────────────────────────────────────────────────────

// null is "no snapshot yet", [] is "this event sells nothing" — the event doc
// and this subcollection are two independent listeners, and the event
// resolves first, so without that distinction the page reads an unarrived
// catalog as sold out on its very first paint.
//
// keepOnClear: a published event's ticket types are public and
// identity-free — a sign-in or sign-out elsewhere in the same tab while this
// event is open must not wipe them. The sole writer is checkout.sync.ts's
// ticketTypes$, a live Firestore snapshot listener that only re-emits when
// the subcollection itself changes, so a clearStates() reset would strand
// this at null with nothing to repopulate it short of leaving and re-entering
// the route.
export const shopTicketTypesState = state<readonly TicketTypeWithId[] | null>(
	"storefront/ticket-types",
).memory(null, { keepOnClear: true });

export type BasketItem = Checkout.Checkout["payload"]["items"][number];

/** The checkout's own payload.items mirror — empty until the buyer picks
 *  something, and their own from there (setQty below). A live cycle adopted
 *  at land restores the doc's own items into it.
 *
 *  keepOnClear: session.sync.ts answers a uid transition with
 *  clearStates(), and the selection is the person's, not the anonymous
 *  session's. Only an event switch clears it (checkout.sync.ts's eventId
 *  leg). */
export const basketItemsState = state<readonly BasketItem[]>(
	"storefront/basket",
).memory([], { keepOnClear: true });

/** The programme day the buyer picked, `YYYY-MM-DD` in the venue's own zone
 *  — <programme-day-selector>'s single write. Null until they pick one, which
 *  is the ordinary state: the land selects nothing and the first still-valid
 *  day resolves out of the data itself. Read through selectedProgrammeDay
 *  below and never directly, since a stored pick outlives the reason it was
 *  valid — the day passes, the catalog reloads, the buyer walks to another
 *  event — and only the resolved value is guaranteed to name a day the shop
 *  actually offers. That resolution is also why nothing resets this on an
 *  event switch: a pick the new event has no day for simply stops winning. */
export const selectedProgrammeDayState = state<string | null>(
	"storefront/programme-day",
).memory(null);

type BuyerContact = NonNullable<Checkout.Checkout["payload"]["buyer"]>;
export type ContactDraft = Pick<BuyerContact, "email" | "name" | "address">;

/** The checkout's own payload.buyer mirror — nullish at create; here as an
 *  always-present draft so the eager contact fields have something to bind
 *  to from first paint. `address` reads back undefined on a draft this
 *  device stored before the field existed — the schema's own nullish is what
 *  makes that the ordinary shape rather than a migration.
 *
 *  `.local()` is this state's binding decision and is not open to
 *  re-derivation: the buyer's own name, email, and address are the one thing
 *  on this page that never changes between purchases, and a device that
 *  forgets them makes a returning buyer retype their identity on every
 *  order. `.memory()` here is a regression, not a stricter reading — the
 *  device-storage act runs on § 25 Abs. 2 Nr. 2 TDDDG's strictly-necessary
 *  exception, on the same footing as checkout.state.ts's own
 *  openCheckoutPointer and existingTicketToken: the buyer expressly
 *  requested this checkout by a deliberate action, and the draft is what
 *  carries their own input across the reload that same checkout survives.
 *  An audit pass that reads this as unconsented tracking has confused the
 *  buyer's own re-presented input with a purpose beyond the requested
 *  service; the operator's privacy policy is where that basis is disclosed,
 *  and it is the surface to correct if the disclosure ever lags this state.
 *
 *  keepOnClear is a separate guarantee on the same field: it protects the
 *  draft from session.sync.ts's clearStates() firing on a uid transition
 *  mid-checkout (a buyer signing in, a second member taking over the
 *  device) — without it, a buyer who already typed their details would lose
 *  them to an identity change that has nothing to do with what they typed.
 *  checkout.sync.ts's edit-write leg emits at subscribe, so the current
 *  draft reaches the checkout's payload.buyer without a keystroke. */
export const contactState = state<ContactDraft>("storefront/contact").local(
	{ email: "", name: "", address: "" },
	{ keepOnClear: true },
);

// ── Pure derivation ──────────────────────────────────────────────────────────

type Bucket = Event.Availability[string]["state"];

function bucketOf(availability: Event.Availability, id: string): Bucket {
	return availability[id]?.state ?? "open"; // absent means open
}

export type SalesWindowStatus = "open" | "notYetOpen" | "closed";

/** Where `now` sits relative to the type's own EFFECTIVE salesWindow —
 *  resolved via TicketType.resolveSalesWindow, never read off the raw field
 *  — carried on every row so a revealed-but-outside-window type states why
 *  it can't be bought instead of rendering a bare, unexplained card. An
 *  inherited window's `opensAt` resolves to null (no lower bound), so it can
 *  never be "not yet open". */
function salesWindowStatus(
	t: TicketTypeWithId,
	event: Pick<Event.Event, "schedule">,
	now: number,
): SalesWindowStatus {
	const window = TicketType.resolveSalesWindow(t, event);
	if (window.opensAt !== null && now < window.opensAt.toMillis())
		return "notYetOpen";
	if (now > window.closesAt.toMillis()) return "closed";
	return "open";
}

function isPurchasable(
	t: TicketTypeWithId,
	event: Pick<Event.Event, "schedule">,
	availability: Event.Availability,
	now: number,
): boolean {
	return (
		salesWindowStatus(t, event, now) === "open" &&
		bucketOf(availability, t.id) !== "soldOut"
	);
}

/** A chained type reveals once its named predecessor is no longer
 *  purchasable; a dangling pointer fails open — shown, never silently
 *  hidden. */
function isRevealed(
	t: TicketTypeWithId,
	types: readonly TicketTypeWithId[],
	event: Pick<Event.Event, "schedule">,
	availability: Event.Availability,
	now: number,
): boolean {
	if (!t.showAfterSoldOut) return true;
	const predecessor = types.find((p) => p.id === t.showAfterSoldOut);
	if (!predecessor) return true;
	return !isPurchasable(predecessor, event, availability, now);
}

export type ShopTypeRow = {
	readonly id: string;
	readonly name: string;
	readonly description: TicketType.TicketType["description"];
	readonly price: TicketType.TicketType["price"];
	readonly volumeDiscounts: TicketType.TicketType["volumeDiscounts"];
	readonly vatRate: TicketType.TicketType["vatRate"];
	readonly bucket: Bucket;
	readonly purchasable: boolean;
	readonly salesWindowStatus: SalesWindowStatus;
	readonly opensAt: ReturnType<typeof TicketType.resolveSalesWindow>["opensAt"];
	readonly closesAt: ReturnType<
		typeof TicketType.resolveSalesWindow
	>["closesAt"];
	/** resolveEffectiveTimezone(t, event, locations) — this type's own venue
	 *  override wins over the event's, so a type in a different-timezone room
	 *  renders its own date, not the event-wide one. */
	readonly timezone: string;
	/** The type's own EFFECTIVE entry window (TicketType.resolveEntryWindow) in
	 *  epoch millis — when it opens the DOOR, as against opensAt/closesAt
	 *  above, which say when it may be BOUGHT. Millis rather than the Timestamp
	 *  because every reader here compares or formats it and none passes it
	 *  back to Firestore. */
	readonly entryOpensAt: number;
	readonly entryClosesAt: number;
	/** Whether that resolved window spans the event's whole schedule
	 *  (TicketType.coversWholeEvent) — true of an absent entryWindow, which
	 *  inherits the schedule, and equally of a span an operator set by hand.
	 *  Such a type is tied to no day: it mints no chip, since one per day it
	 *  covers would each lead back to the same single row, and it renders in
	 *  the section above the chips. Everything else is filed under the days
	 *  its own window covers. */
	readonly coversWholeEvent: boolean;
	readonly qty: number;
	readonly canIncrement: boolean;
	readonly canDecrement: boolean;
};

/** A row's own day set, in its own venue zone — TicketType.daysCovered
 *  (@momo/types) is the one derivation the shop, the console and the setup
 *  form all read, so no two of them can answer "which day is this type for?"
 *  differently; this only feeds it the row's already-resolved window. */
function daysOf(
	row: Pick<ShopTypeRow, "entryOpensAt" | "entryClosesAt" | "timezone">,
): readonly string[] {
	return TicketType.daysCovered(
		{ opensAt: row.entryOpensAt, closesAt: row.entryClosesAt },
		row.timezone,
	);
}

/** Pure — every buyer-claimable ticket-kind type currently revealed by its
 *  own showAfterSoldOut chain, augmented with its live bucket, purchasability,
 *  salesWindow status, basket qty, and stepper affordance. `event` supplies
 *  the schedule every inheriting type's window resolves against
 *  (TicketType.resolveSalesWindow); `null` while the event hasn't loaded
 *  yet yields no rows, since there's nothing to resolve against — an
 *  invariant that already holds because `types` is empty until the event
 *  is ready. Exported for its own parameterized test suite, which drives
 *  the purchasability rule directly rather than through the computed.
 *
 *  `locale` resolves every row's name and description out of the type's own
 *  per-language copy, falling back to the authored default when that language
 *  carries none.
 *
 *  `unlockedTypeIds` is the checkout's own `voucherResult`, and the whole of
 *  what makes a hidden ticket type visible: a type whose claimableBy resolves
 *  "code" is dropped from the shop until this array names it. Hidden, never
 *  listed-and-locked — a row a buyer cannot buy, standing on the page before
 *  they hold a code, states a rule they have no way to act on and no reason to
 *  have been told. The reveal still moves nothing under the buyer's eye,
 *  because they are inside the entry overlay when it lands: the list
 *  re-renders behind the scrim, the overlay's own confirmation names what was
 *  revealed, and it closes onto a list that already carries the row.
 *
 *  `selectedDay` is a `YYYY-MM-DD` key in the venue's own zone (see
 *  programmeDays below) and drops the day-bound types that do not admit on
 *  it. Null is a real answer, not a missing argument: an event carrying no
 *  day-bound type at all — the common case, and the whole of today's catalog
 *  — has no day dimension, so nothing is filtered and the shop lists what it
 *  has always listed. */
export function computeShopTypeRows(
	types: readonly TicketTypeWithId[],
	event: Pick<Event.Event, "schedule" | "locationId"> | null,
	availability: Event.Availability,
	basket: readonly BasketItem[],
	maxTicketsPerOrder: number,
	now: number,
	locations: Record<string, Pick<Location.Location, "timezone">>,
	locale: Locale,
	unlockedTypeIds: readonly string[] = [],
	selectedDay: string | null = null,
): readonly ShopTypeRow[] {
	if (!event) return [];
	const totalQty = basket.reduce((sum, i) => sum + i.qty, 0);
	const atCap = totalQty >= maxTicketsPerOrder;

	return (
		types
			// The operator's own chosen order, admin-side and here alike — a
			// buyer sees types in the same sequence the operator dragged them
			// into, never a Firestore snapshot's incidental doc order.
			.toSorted((a, b) => a.order - b.order || a.name.localeCompare(b.name))
			// Presentation only, never protection — settle refuses a buyer-arm line
			// naming a staff-only type on its own authority. The audience term
			// drops ROWS, never the `types` argument: isRevealed above fails
			// open on a predecessor it cannot find, so pre-filtering the input
			// does not hide a successor, it reveals one — a type gated behind a
			// staff-only predecessor stops resolving it and shows in the public
			// shop at once, silently. Keep the term here, on the output.
			.filter((t) => {
				const claimableBy = TicketType.resolveClaimableBy(t);
				return (
					t.kind === "ticket" &&
					(claimableBy === "buyer" ||
						(claimableBy === "code" && unlockedTypeIds.includes(t.id))) &&
					isRevealed(t, types, event, availability, now)
				);
			})
			.map((t) => {
				const qty = basket.find((b) => b.typeId === t.id)?.qty ?? 0;
				const purchasable = isPurchasable(t, event, availability, now);
				const window = TicketType.resolveSalesWindow(t, event);
				const entry = TicketType.resolveEntryWindow(t, event);
				return {
					id: t.id,
					name: resolveLocalizedText(t.nameByLocale, t.name, locale),
					description: resolveLocalizedText(
						t.descriptionByLocale,
						t.description,
						locale,
					),
					price: t.price,
					volumeDiscounts: t.volumeDiscounts,
					vatRate: t.vatRate,
					bucket: bucketOf(availability, t.id),
					purchasable,
					salesWindowStatus: salesWindowStatus(t, event, now),
					opensAt: window.opensAt,
					closesAt: window.closesAt,
					timezone: TicketType.resolveEffectiveTimezone(t, event, locations),
					entryOpensAt: entry.opensAt.toMillis(),
					entryClosesAt: entry.closesAt.toMillis(),
					coversWholeEvent: TicketType.coversWholeEvent(
						{
							opensAt: entry.opensAt.toMillis(),
							closesAt: entry.closesAt.toMillis(),
						},
						event,
					),
					qty,
					canIncrement: purchasable && !atCap,
					canDecrement: qty > 0,
				};
			})
			// The day term drops ROWS, last, and only day-bound ones — off the
			// row's own resolved window, so the chips and this list can never
			// derive membership differently. A row tied to no day survives
			// every day: it stands in the section above the chips, and staying
			// there on each of them is the point of that section.
			.filter(
				(r) =>
					selectedDay === null ||
					r.coversWholeEvent ||
					daysOf(r).includes(selectedDay),
			)
	);
}

const shopTypeRowsOn = (selectedDay: string | null) =>
	computeShopTypeRows(
		shopTicketTypesState.value ?? [],
		eventDetailState.value.event,
		eventDetailState.value.event?.availability ?? {},
		basketItemsState.value,
		eventDetailState.value.event?.maxTicketsPerOrder ?? Infinity,
		Date.now(),
		Object.fromEntries(locationsState.value.map((l) => [l.id, l] as const)),
		uiLanguage(),
		appliedVoucher.get()?.unlockedTypeIds ?? [],
		selectedDay,
	);

/** Every row the shop offers for this event, the day dimension ignored — the
 *  BASKET's own authority, which is why it is the day-agnostic one:
 *  orderLines and orderTotal below read it, and a line bought for Friday
 *  stays in the order (and in the amount the buyer pays) while they browse
 *  Saturday. What the list renders is selectedDayRows. */
export const shopTypeRows = computed<readonly ShopTypeRow[]>(() =>
	shopTypeRowsOn(null),
);

/** One calendar day the shop sells entry for, in the venue's own zone —
 *  TicketType.ProgrammeDay (@momo/types), the shape <programme-day-selector>
 *  takes and every surface carrying the chip row derives. */
export type ProgrammeDay = TicketType.ProgrammeDay;

/** The day chips, sorted, derived and never stored: every day-bound type's
 *  own days, each in its own venue zone (TicketType.programmeDaysOf, the one
 *  derivation the shop, the console and the setup form all read).
 *
 *  A day-bound type is what MAKES the row, so every chip leads to at least
 *  the type that minted it. With none, nobody has filed anything under a day:
 *  no chips, no selector, and the page the shop rendered before the day
 *  dimension existed, to the pixel — the common case, where no type carries a
 *  stored window at all. A type tied to no day mints nothing here and stands
 *  above the row instead (wholeEventRows), since one chip per day it covers
 *  would lead back to the same single row.
 *
 *  A past day is HIDDEN, not struck: once its own door has shut the chip
 *  leads to a list of sale-ended rows, and it would spend the scroller's
 *  first slots doing it. The event's true extent is already stated once, in
 *  the header's schedule line. */
export const programmeDays = computed<readonly ProgrammeDay[]>(() => {
	const now = Date.now();
	return TicketType.programmeDaysOf(
		shopTypeRows
			.get()
			.filter((r) => !r.coversWholeEvent)
			.map((r) => ({
				opensAt: r.entryOpensAt,
				closesAt: r.entryClosesAt,
				timezone: r.timezone,
			})),
	).filter((d) => d.closesAt >= now);
});

/** The day the shop is showing: the buyer's own pick while it is still a
 *  programme day, else the first one that is — which is also what a fresh
 *  land resolves to with nothing written anywhere, so the first still-valid
 *  day is selected on load and a pick that stops naming a day (its window
 *  closed while the page stood open, the catalog reloaded, the buyer moved to
 *  another event) heals itself instead of stranding the list empty. Null
 *  exactly when this event has no programme days at all. */
export const selectedProgrammeDay = computed<string | null>(() =>
	TicketType.resolveProgrammeDay(
		programmeDays.get(),
		selectedProgrammeDayState.value,
		Date.now(),
	),
);

/** What the list BENEATH the chips renders: the types bound to the selected
 *  day, and nothing else. Everything tied to no day stands above the chips
 *  instead (wholeEventRows), so each of the two sections is one answer to
 *  "what is this ticket good for?" and no row appears in both. With no
 *  programme days there is no day to be bound to, so every row is simply a
 *  row and the section above stays empty. */
export const selectedDayRows = computed<readonly ShopTypeRow[]>(() => {
	const day = selectedProgrammeDay.get();
	if (day === null) return shopTypeRows.get();
	return shopTypeRowsOn(day).filter((r) => !r.coversWholeEvent);
});

/** The programme days the order already holds a ticket for — the reserved dot
 *  on each chip, and the one thing a multi-day order needs the chip row to
 *  tell it, since switching days never touches the basket. Only a line filed
 *  under a day marks anything: a type whose window covers the whole schedule
 *  is tied to no single day, so it dots no chip. */
export const basketProgrammeDays = computed<ReadonlySet<string>>(() => {
	const days = new Set<string>();
	for (const row of shopTypeRows.get()) {
		if (row.qty === 0 || row.coversWholeEvent) continue;
		for (const day of daysOf(row)) days.add(day);
	}
	return days;
});

/** The section ABOVE the chips: every type tied to no single day, its
 *  resolved window covering the whole schedule however that window was
 *  authored. They are the whole price ladder, and they read the same under
 *  every chip, so they stand once at the top instead of repeating inside each
 *  day's list, where two €10 rows side by side would say nothing about which
 *  of them is good for which day. Day-agnostic by construction — read off
 *  shopTypeRows, never the day-filtered set — so tapping across days moves
 *  only the list beneath the chips. It exists only where there are days to be
 *  outside of. */
export const wholeEventRows = computed<readonly ShopTypeRow[]>(() =>
	selectedProgrammeDay.get() === null
		? []
		: shopTypeRows.get().filter((r) => r.coversWholeEvent),
);

export type LayoutMode =
	| "loading"
	| "notOnSale"
	| "soldOut"
	| "daySoldOut"
	| "single"
	| "multi";

/** The collapsed page's loading/single/multi/sold-out branch — "loading"
 *  while either half of the catalog is still in flight: the event itself
 *  (eventDetailState's own status) or the ticket types (a null
 *  shopTicketTypesState, no snapshot yet). Never inferred from an empty
 *  shopTypeRows, since an unarrived event OR an unarrived type list also
 *  yields zero rows and would otherwise flash "soldOut". Once both are ready,
 *  the branch is the count of currently-purchasable revealed ticket-kind
 *  types, never a stored mode field.
 *
 *  Two sold-out states, because the two suppress different things.
 *  "soldOut" stays EVENT-scoped — nothing purchasable on any day, the section
 *  above the chips included — since it is what nothingToBuy below takes the
 *  contact fields, the payment section and the jump bar away on, and none of
 *  those may go while another day still sells. "daySoldOut" is this day and
 *  this day only: the list beneath the chips says so, every other chip still
 *  leads somewhere, and the page below stays exactly where the buyer left
 *  it.
 *
 *  "notOnSale" is the event-scoped state with no row at all: nothing in the
 *  catalog is offered to a stranger with the link (every type is for staff,
 *  behind a code, or an add-on). Nothing sold out, so saying "sold out" there
 *  tells the buyer a story that never happened — a free allocation answered
 *  nowhere resolves staff-only, and its event reads as gone on its first
 *  day. */
export const layoutMode = computed<LayoutMode>(() => {
	if (
		eventDetailState.value.status === "loading" ||
		shopTicketTypesState.value === null
	)
		return "loading";
	const rows = shopTypeRows.get();
	if (rows.length === 0) return "notOnSale";
	if (!rows.some((r) => r.purchasable)) return "soldOut";
	const purchasableCount = selectedDayRows
		.get()
		.filter((r) => r.purchasable).length;
	if (purchasableCount === 0) return "daySoldOut";
	return purchasableCount === 1 ? "single" : "multi";
});

/** Nothing on this event can be bought right now, whichever of the two
 *  event-scoped reasons holds — what takes the contact fields, the payment
 *  section and the jump bar away. */
export const nothingToBuy = computed(
	() => layoutMode.get() === "soldOut" || layoutMode.get() === "notOnSale",
);

/** The event to hand the return page, or null when this header offers no way
 *  back — the same reading of the window the landing card and the return page
 *  itself make, so the three agree on every render. */
export const refundEntryEventId = computed<string | null>(() => {
	const event = eventDetailState.value.event;
	if (event === null) return null;
	return refundWindowPhase(event) === "open" ? event.id : null;
});

export const totalBasketQty = computed(() =>
	basketItemsState.value.reduce((sum, i) => sum + i.qty, 0),
);

export const atOrderCap = computed(() => {
	const cap = eventDetailState.value.event?.maxTicketsPerOrder ?? Infinity;
	return totalBasketQty.get() >= cap;
});

/** The basket, as money lines. TicketLine, never the OrderLine union: it is
 *  mapped from shopTypeRows — ticket types — so an admission is the only
 *  thing it can hold. The union's other two kinds are minted server-side by
 *  settle (the voucher's discount split, the operator's booking fee) and
 *  reach the buyer on the order, never through here. */
export const orderLines = computed<readonly Order.TicketLine[]>(() =>
	shopTypeRows
		.get()
		.filter((r) => r.qty > 0)
		.map((r) => {
			const unitPrice = TicketType.resolveUnitPrice(r, r.qty);
			return {
				kind: "ticket" as const,
				typeId: r.id,
				qty: r.qty,
				unitPrice,
				vatRate: r.vatRate,
				amount: unitPrice * r.qty,
			};
		}),
);

/** The basket's ticket lines summed — the gross BEFORE any code or fee, and
 *  the only figure this browser authors. checkout.sync.ts's edit-write
 *  compares it against the document's own ticket lines, never against
 *  orderTotal: a discount line and a booking-fee line are both the server's
 *  own additions to pricedLines, present on the document and absent from
 *  every write made here, so neither belongs on either side of a question
 *  about what this client would write. */
export const basketGross = computed(() =>
	orderLines.get().reduce((sum, l) => sum + l.amount, 0),
);

/** What the buyer actually owes: the basket's gross, less what a code that
 *  STANDS takes off it, plus the operator's booking fee on top.
 *
 *  Neither term is presentation. `pricedLines` carries the server's own
 *  discount and booking-fee lines, and two readers measure against a total
 *  derived from that array — amountInSync gates the pay control on
 *  `payment.syncedAmount` equalling this, and § 33 UStDV's address ceiling
 *  sits on it, at the same cent settle measures its own. A total short of
 *  either term gates the pay control shut and states a price the buyer is
 *  not charged.
 *
 *  The fee whole, never per-rate: preparePayment appends
 *  `splitFeeByRate(fee.amount, …)`, whose parts sum back to `fee.amount` to
 *  the cent at a positive net — and the split is unreachable here anyway,
 *  since apportioning by rate needs the server's own discount LINES where
 *  this browser holds one summed figure.
 *
 *  `net <= 0` is the free arm, splitFeeByRate's own guard mirrored: a fee on
 *  a zero basket hides the free-confirm control behind a PSP session settle
 *  never asks for. A workspace card still in flight prices fee-less, and is
 *  safe to — settle's amount carries the fee, so `payment.syncedAmount`
 *  cannot equal a fee-less total and the pay control stays curtained until
 *  the card lands. */
export const orderTotal = computed(() => {
	const net = basketGross.get() - (appliedVoucher.get()?.discountCents ?? 0);
	const workspace = workspaceCard.value.workspace;
	const event = eventDetailState.value.event;
	if (net <= 0 || workspace === null || event === null) return net;
	return net + BookingFee.resolveBookingFee(workspace, event).amount;
});

/** Whether the buyer has picked anything at all. Counted in lines, never in
 *  the total: a free ticket is a real selection at a zero total, and the
 *  contact fields and the free-confirm arm belong to it exactly as they
 *  belong to a paid basket. */
export const basketFilled = computed(() => orderLines.get().length > 0);

/** Whether what this order will be CHARGED puts the invoice above § 33
 *  UStDV's Kleinbetragsrechnung ceiling, where § 14 Abs. 4 Nr. 1 starts
 *  demanding the recipient's address. Live off orderTotal — the booking fee
 *  inside it, since settle measures its own ceiling on an authoritative gross
 *  that carries the fee — so the field appears and the pay gesture starts
 *  requiring it at the same instant, and a decrement back under the ceiling
 *  releases both. */
export const addressRequired = computed(() =>
	Order.needsRecipientAddress(orderTotal.get()),
);

/** The buyer form is name and email — both always required before the pay
 *  gesture activates — plus the address, required only while the total sits
 *  above the ceiling. Matches BuyerContactSchema's own required set (locale is
 *  never a field in this form — it follows the header's own language toggle;
 *  address is nullish there because below the ceiling absent IS the correct
 *  value). */
export const contactValid = computed(
	() =>
		// The SAME validator BuyerContactSchema.email enforces at the write —
		// a looser hand-rolled regex here would pass a value the checkout
		// doc's own schema then rejects on read, writing (and re-writing on
		// every later snapshot) a doc the buyer's own listener can't parse.
		Checkout.BuyerContactSchema.shape.email.safeParse(
			contactState.value.email.trim(),
		).success &&
		contactState.value.name.trim().length > 0 &&
		(!addressRequired.get() ||
			(contactState.value.address ?? "").trim().length > 0),
);

// ── Actions ──────────────────────────────────────────────────────────────────

/** Sets one type's basket qty, clamped to [0, maxTicketsPerOrder − every
 *  other line's qty]. A qty of 0 splices the line out, the last line
 *  included: the basket returns to the empty state the page opens in, and
 *  checkout.sync.ts holds the open document at its last priced amount until
 *  the next pick re-prices it. */
export function setQty(typeId: string, qty: number) {
	const cap = eventDetailState.value.event?.maxTicketsPerOrder ?? Infinity;
	basketItemsState.update((items) => {
		const otherTotal = items
			.filter((i) => i.typeId !== typeId)
			.reduce((sum, i) => sum + i.qty, 0);
		const clamped = Math.max(0, Math.min(qty, cap - otherTotal));
		if (clamped <= 0) {
			const removeIdx = items.findIndex((i) => i.typeId === typeId);
			if (removeIdx >= 0) items.splice(removeIdx, 1);
			return;
		}
		const existing = items.find((i) => i.typeId === typeId);
		if (existing) existing.qty = clamped;
		else items.push({ typeId, qty: clamped });
	});
}

export function setContact(patch: Partial<ContactDraft>) {
	contactState.set(patch);
}
